import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Allotment, AllotmentHandle } from 'allotment'
import 'allotment/dist/style.css'
import {
  CreditCard,
  Settings,
  LogOut,
  User,
  Bot,
  MessageSquare,
  BookOpen,
  RefreshCw,
  CheckCircle,
  Copy,
  AlertCircle,
  Key,
  Trash2,
  Gift,
  Loader2,
  Menu,
  Check,
  X,
  Pencil,
  Mail,
  Download,
  ChevronLeft,
  LayoutGrid,
  ExternalLink,
  Coins,
  Zap,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useLogout } from '@/lib/api/hooks'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ThemeToggle } from '@/components/theme-toggle'
import { Logo } from '@/components/logo'
import { AvailableToolsTab } from '@/components/dashboard/AvailableToolsTab'
import { DiscordInviteCard } from '@/components/dashboard/DiscordInviteCard'
import { CreditLedger } from '@/components/dashboard/CreditLedger'
import { CreditUsageHistory } from '@/components/dashboard/CreditUsageHistory'
import { GettingStartedPanel } from '@/components/dashboard/GettingStartedPanel'
import { ChatInterface } from '@/components/chat'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet'
import {
  useUserTier,
  useDetailedUsage,
  useApiActivity,
  useCallPackPricing,
  useApiTokens,
  useOAuthTokens,
  useCallPackCheckout,
  useTiersWithTools,
  useDeleteAccount,
  useEmailPreferences,
  useUpdateEmailPreferences,
  useDashboardBalances,
  useCreditUsageBreakdown,
  useCreditLedger,
  useCreditUsageHistory,
  useChatModels,
} from '@/lib/api/hooks'
import type { Conversation } from '@/lib/api/chat'
import { formatTierName, mapCallPack } from '@/lib/utils/dashboardMappers'
import { userAPI, subscriptionAPI, apiClient } from '@/lib/api/client'
import { cn } from '@/lib/utils'
import { useChat } from '@/hooks/chat/useChat'
import { ConversationList } from '@/components/chat/ConversationList'
import { CollapsedSidebarStrip } from '@/components/layout'
import { useLayoutStore, SIDEBAR_CONSTRAINTS } from '@/store/layoutStore'

// Shared view-model types (frontend projections of loosely-typed API data)
interface DashboardStats {
  currentUsage: number
  callPackBalance: number
  totalAvailable: number
}

interface UserTierView {
  id: number | string
  name: string
  displayName: string
  monthlyLimit: number
  currentUsage: number
  databases: string[]
  toolCount: number
}

interface ApiError {
  message?: string
  response?: { status?: number; data?: { message?: string; detail?: string } }
}

interface TokenView {
  id: number
  name?: string
  token?: string
  is_active?: boolean
  last_used_at?: string | null
}

interface CreditUsageBreakdown {
  month_label?: string
  breakdown?: Array<{ usage_type: string; credits: number; label: string }>
}

interface ChatModelTier {
  credit_cost: number
  display_name: string
}

interface ActivityDetail {
  tool_name?: string
  database_name?: string
  success?: boolean
  created_at: string
  error_message?: string
  response_time_ms?: number
  status_code?: number
  request_params?: string | Record<string, unknown> | null
}

// Sidebar Component
interface SidebarContentProps {
  activeView: string
  setActiveView: (view: string) => void
  stats: DashboardStats
  isCollapsed: boolean
  // Chat props
  conversations: Conversation[]
  currentConversationId: string | null
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onDeleteConversation: (id: string) => void
  onUpdateConversation?: (id: string, title: string) => Promise<void>
  isLoadingConversations: boolean
  // Feature flags
  onNavigateToWorkspace?: () => void
}

const SidebarContent = ({
  activeView,
  setActiveView,
  stats,
  isCollapsed,
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onUpdateConversation,
  isLoadingConversations,
  onNavigateToWorkspace,
}: SidebarContentProps) => {
  const navItems = [
    {
      value: 'chat',
      label: 'Playground',
      icon: MessageSquare,
      tooltip: 'Test MCP tools in an interactive chat environment',
    },
    {
      value: 'plan',
      label: 'Account',
      icon: Coins,
      tooltip: 'Credit balance, activity, and account overview',
    },
    { value: 'tools', label: 'Tools', icon: Bot, tooltip: 'Browse all available LCA tools' },
    {
      value: 'usage',
      label: 'Credits',
      icon: CreditCard,
      tooltip: 'Purchase credits and redeem promo codes',
    },
  ]

  return (
    <div className="flex flex-col h-full w-full">
      {/* Navigation - More compact */}
      <nav className="flex-shrink-0 p-3 pt-10 space-y-0.5 border-b relative">
        {navItems.map((item) => (
          <Tooltip key={item.value}>
            <TooltipTrigger asChild>
              <button
                onClick={() => setActiveView(item.value)}
                className={cn(
                  'w-full flex items-center space-x-2.5 px-3 py-2.5 rounded-lg transition-all text-left text-sm relative group',
                  activeView === item.value
                    ? 'bg-muted/30 text-primary font-semibold shadow-sm ring-1 ring-primary/20'
                    : 'text-muted-foreground hover:bg-muted/20 hover:text-foreground hover:translate-x-1',
                )}
              >
                <item.icon
                  className={cn(
                    'w-4 h-4 flex-shrink-0 transition-transform group-hover:scale-110',
                    activeView === item.value ? 'text-primary' : 'text-muted-foreground',
                  )}
                />
                {!isCollapsed && <span>{item.label}</span>}
                {activeView === item.value && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute left-0 w-1 h-4 bg-primary rounded-r-full"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{item.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        ))}

        {/* Workspace - opens in new tab */}
        {
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onNavigateToWorkspace}
                className={cn(
                  'w-full flex items-center space-x-2.5 px-3 py-2.5 rounded-lg transition-all text-left text-sm relative group mt-2 border-t pt-4',
                  'text-muted-foreground hover:bg-muted/20 hover:text-foreground hover:translate-x-1',
                )}
              >
                <LayoutGrid className="w-4 h-4 flex-shrink-0 transition-transform group-hover:scale-110 text-muted-foreground" />
                {!isCollapsed && (
                  <>
                    <span>Workspace</span>
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Visual LCA project workspace (opens in new tab)</p>
            </TooltipContent>
          </Tooltip>
        }
      </nav>

      {/* Chat Conversations - Only show when chat is active */}
      {activeView === 'chat' && !isCollapsed && (
        <div className="flex-1 overflow-hidden border-b">
          <ConversationList
            conversations={conversations}
            currentConversationId={currentConversationId}
            onSelectConversation={onSelectConversation}
            onNewConversation={onNewConversation}
            onDeleteConversation={onDeleteConversation}
            onUpdateConversation={onUpdateConversation}
            isLoading={isLoadingConversations}
          />
        </div>
      )}

      {/* Stats - Only show when not collapsed */}
      {!isCollapsed && (
        <div className="flex-shrink-0 p-3 space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Credits</span>
            <span className="font-semibold text-success">
              {stats.callPackBalance.toLocaleString()}
            </span>
          </div>

          {/* Discord Community Invite - Only show when NOT in chat view */}
          {activeView !== 'chat' && <DiscordInviteCard compact />}
        </div>
      )}
    </div>
  )
}

// Plan Overview Component
interface PlanOverviewContentProps {
  stats: DashboardStats
  userTier: UserTierView
  creditUsageBreakdown?: CreditUsageBreakdown
}

const PlanOverviewContent = ({
  stats,
  userTier,
  creditUsageBreakdown,
}: PlanOverviewContentProps) => {
  const totalCredits = stats.callPackBalance
  const totalTools = userTier.toolCount
  const breakdown = creditUsageBreakdown?.breakdown || []
  return (
    <>
      {/* Credit Balance Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Credit Balance</p>
                <div className="text-4xl font-bold text-success">
                  {totalCredits.toLocaleString()}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xl font-bold">{userTier.databases.length}</div>
                  <div className="text-xs text-muted-foreground">Databases</div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xl font-bold">{totalTools}</div>
                  <div className="text-xs text-muted-foreground">Tools</div>
                </div>
              </div>
            </div>
            {/* Usage Breakdown - Calendar month */}
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground mb-3">
                Credits used — {creditUsageBreakdown?.month_label || 'this month'}
              </p>
              <div className="grid grid-cols-3 gap-3 text-center">
                {breakdown.map((item) => (
                  <div key={item.usage_type} className="p-2 bg-muted/30 rounded-lg">
                    <div className="text-lg font-bold">{item.credits}</div>
                    <div className="text-xs text-muted-foreground">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent MCP Activity - removed, will use Live Workspace instead */}
    </>
  )
}

const Dashboard = () => {
  const navigate = useNavigate()
  const [activeView, setActiveView] = useState('chat')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Use layout store for persistent sidebar state
  const { sidebarCollapsed, sidebarWidth, toggleSidebar, setSidebarWidth } = useLayoutStore()
  const allotmentRef = useRef<AllotmentHandle>(null)

  // Getting Started panel is disabled pending a redesign; keep the code path wired.
  const showGettingStartedPanel = false

  // Navigate to workspace handler
  const handleNavigateToWorkspace = () => {
    window.open('/workspace', '_blank')
  }

  // Chat hooks
  const {
    conversations,
    currentConversationId,
    messages,
    streamingContent,
    isStreaming,
    isLoadingConversations,
    isLoadingMessages,
    error: chatError,
    failedMessageId,
    loadConversations,
    loadMessages,
    startNewConversation,
    sendMessage,
    retryMessage,
    deleteConversation,
    updateConversationTitle,
  } = useChat()

  // Load conversations on mount only; loadConversations/conversations.length are
  // intentionally excluded so this runs exactly once.
  useEffect(() => {
    if (conversations.length === 0) {
      loadConversations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Token management state
  const [showCreateTokenModal, setShowCreateTokenModal] = useState(false)
  const [showTokenDisplayModal, setShowTokenDisplayModal] = useState(false)
  const [newTokenData, setNewTokenData] = useState<{ token: string; name: string } | null>(null)
  const [tokenName, setTokenName] = useState('')
  const [tokenToDelete, setTokenToDelete] = useState<number | null>(null)
  const [purchasingPackId, setPurchasingPackId] = useState<number | null>(null)
  const [selectedActivity, setSelectedActivity] = useState<ActivityDetail | null>(null)

  // Credit quantity state for call pack purchases
  const [creditQuantity, setCreditQuantity] = useState(1)

  // Delete account state
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [showGoodbyeDialog, setShowGoodbyeDialog] = useState(false)

  // Promo code state
  const [promoCode, setPromoCode] = useState('')
  const [promoValidation, setPromoValidation] = useState<{
    isValid: boolean
    message: string
  } | null>(null)
  const [isValidatingPromo, setIsValidatingPromo] = useState(false)
  const [isRedeemingPromo, setIsRedeemingPromo] = useState(false)

  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [isSavingName, setIsSavingName] = useState(false)

  const queryClient = useQueryClient()
  const logoutMutation = useLogout()

  // STEP 4: Real user tier data from backend
  const { data: userTierData, isLoading: tierLoading, error: tierError } = useUserTier()

  // STEP 5: Real usage data from backend
  const { data: usageData, isLoading: usageLoading, error: usageError } = useDetailedUsage()

  // Dashboard balances (includes active tier promotion)
  const { data: dashboardBalances } = useDashboardBalances()

  // Credit usage breakdown (last 30 days)
  const { data: creditUsageBreakdown } = useCreditUsageBreakdown()

  const { data: creditLedgerData } = useCreditLedger()
  const { data: creditUsageHistoryData } = useCreditUsageHistory()
  const { data: chatModels } = useChatModels()

  // STEP 6: Real API activity from backend
  const { isLoading: activityLoading, error: activityError } = useApiActivity({
    limit: 10,
    hours: 24,
  })

  // Call pack / credit pricing from backend
  const {
    data: callPackPricingData,
    isLoading: pricingLoading,
    error: pricingError,
  } = useCallPackPricing()

  // STEP 9: Real API tokens from backend
  const { data: apiTokensData, isLoading: tokensLoading, error: tokensError } = useApiTokens()

  // OAuth tokens (read-only display)
  const { data: oauthTokensData, isLoading: oauthTokensLoading } = useOAuthTokens()

  // Tiers with tools data
  const { data: tiersWithToolsData, isLoading: tiersWithToolsLoading } = useTiersWithTools()

  // Check for 401 errors and redirect to auth immediately
  useEffect(() => {
    const errors = [tierError, usageError, activityError, pricingError, tokensError]
    const has401 = errors.some((error) => {
      // Check for axios error with status 401
      if (error && typeof error === 'object') {
        const axiosError = error as ApiError
        return (
          axiosError.response?.status === 401 ||
          axiosError.message?.includes('401') ||
          axiosError.message?.toLowerCase().includes('unauthorized')
        )
      }
      return false
    })

    if (has401) {
      localStorage.removeItem('auth_token')
      navigate('/auth', { replace: true })
    }
  }, [tierError, usageError, activityError, pricingError, tokensError, navigate])

  // Low credit balance warning
  const [hasShownUsageWarning, setHasShownUsageWarning] = useState(false)
  useEffect(() => {
    if (!tierLoading && !usageLoading && usageData && !hasShownUsageWarning) {
      const creditBalance = usageData.call_pack_balance || 0
      if (creditBalance > 0 && creditBalance <= 20) {
        toast.warning(`You have ${creditBalance} credits remaining.`, {
          duration: 6000,
          action: { label: 'Buy Credits', onClick: () => setActiveView('usage') },
        })
        setHasShownUsageWarning(true)
      }
    }
  }, [tierLoading, usageLoading, usageData, hasShownUsageWarning, setActiveView])

  // Token mutations
  const createTokenMutation = useMutation({
    mutationFn: (name: string) => userAPI.createApiToken(name),
    onSuccess: (response) => {
      setShowCreateTokenModal(false)
      setNewTokenData({
        token: response.data.token,
        name: response.data.name,
      })
      setShowTokenDisplayModal(true)
      queryClient.invalidateQueries({ queryKey: ['user', 'api-tokens'] })
      setTokenName('')
      toast.success('API token created successfully!')
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || 'Failed to create token')
    },
  })

  const deleteTokenMutation = useMutation({
    mutationFn: (tokenId: number) => userAPI.deleteApiToken(tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'api-tokens'] })
      setTokenToDelete(null)
      toast.success('Token deleted successfully')
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || 'Failed to delete token')
      setTokenToDelete(null)
    },
  })

  // Delete account mutation
  const deleteAccountMutation = useDeleteAccount()

  // Email preferences
  const { data: emailPrefsData, isLoading: emailPrefsLoading } = useEmailPreferences()
  const updateEmailPrefsMutation = useUpdateEmailPreferences()

  const handleEmailPrefToggle = (key: string, value: boolean) => {
    updateEmailPrefsMutation.mutate({ [key]: value })
  }

  // Promo code handlers
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return
    setIsValidatingPromo(true)
    setPromoValidation(null)
    try {
      const response = await apiClient.post('/promotional-codes/validate', {
        code: promoCode.trim(),
      })
      setPromoValidation({ isValid: response.data.is_valid, message: response.data.message })
    } catch (error) {
      setPromoValidation({
        isValid: false,
        message: (error as ApiError).response?.data?.detail || 'Failed to validate code',
      })
    } finally {
      setIsValidatingPromo(false)
    }
  }

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return
    setIsRedeemingPromo(true)
    try {
      const response = await apiClient.post('/promotional-codes/redeem', { code: promoCode.trim() })
      toast.success(response.data.message)
      setPromoCode('')
      setPromoValidation(null)
      queryClient.invalidateQueries({ queryKey: ['user-tier'] })
      queryClient.invalidateQueries({ queryKey: ['detailed-usage'] })
      queryClient.invalidateQueries({ queryKey: ['user', 'dashboard-balances'] })
    } catch (error) {
      toast.error((error as ApiError).response?.data?.detail || 'Failed to redeem code')
    } finally {
      setIsRedeemingPromo(false)
    }
  }

  // Token handlers
  const handleCreateToken = () => {
    if (!tokenName.trim()) {
      toast.error('Please enter a token name')
      return
    }
    createTokenMutation.mutate(tokenName)
  }

  const handleCopyToken = async (tokenText: string) => {
    try {
      await navigator.clipboard.writeText(tokenText)
      toast.success('Token copied to clipboard!')
    } catch {
      toast.error('Failed to copy token')
    }
  }

  // Name update handlers
  const handleStartEditName = () => {
    setEditedName(userTierData?.name || '')
    setIsEditingName(true)
  }

  const handleCancelEditName = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      toast.error('Name cannot be empty')
      return
    }

    if (editedName === userTierData?.name) {
      setIsEditingName(false)
      return
    }

    setIsSavingName(true)
    try {
      await userAPI.updateProfile({ name: editedName.trim() })
      toast.success('Name updated successfully!')
      setIsEditingName(false)
      queryClient.invalidateQueries({ queryKey: ['user', 'tier'] })
    } catch (error) {
      console.error('Error updating name:', error)
      toast.error((error as ApiError).response?.data?.detail || 'Failed to update name')
    } finally {
      setIsSavingName(false)
    }
  }

  // Delete account handler
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm')
      return
    }

    try {
      await deleteAccountMutation.mutateAsync()
      setShowDeleteAccountDialog(false)
      setDeleteConfirmText('')
      setShowGoodbyeDialog(true)
    } catch {
      // Error is already handled by the mutation's onError
    }
  }

  const handleGoodbyeComplete = () => {
    // Clear all local data
    localStorage.clear()

    // Redirect to landing page
    window.location.href = '/'
  }

  // Export user data handler (GDPR compliance)
  const handleExportData = async () => {
    try {
      toast.loading('Preparing your data export...')
      const response = await userAPI.exportUserData()

      // Create a blob from the JSON data
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      link.download = `ecosemantic-data-export-${timestamp}.json`

      // Trigger download
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.dismiss()
      toast.success('Your data has been downloaded successfully!')
    } catch (error) {
      toast.dismiss()
      toast.error((error as ApiError).response?.data?.detail || 'Failed to export data')
    }
  }

  // Paddle customer portal mutation
  const portalSessionMutation = useMutation({
    mutationFn: () => subscriptionAPI.createPortalSession(),
    onSuccess: (response) => {
      const portalUrl = response.data.portal_url
      window.open(portalUrl, '_blank')
      toast.success('Opening Paddle customer portal...')
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.detail || 'Failed to open customer portal')
    },
  })

  // Paddle portal handler
  const handleManagePaddle = () => {
    portalSessionMutation.mutate()
  }

  // Refresh handler - invalidate all queries to refetch data
  const [isRefreshing, setIsRefreshing] = useState(false)
  const handleRefresh = async () => {
    setIsRefreshing(true)
    await queryClient.invalidateQueries()
    // Refresh chat conversations
    await loadConversations()
    toast.success('Dashboard refreshed')
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  // Logout handler
  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Logged out successfully')
        navigate('/auth')
      },
      onError: () => {
        toast.error('Failed to logout')
      },
    })
  }

  // Chat handlers
  const handleNewConversation = () => {
    // Only show an empty chat view — do NOT create a conversation up-front.
    // The conversation is created when the user sends the first message, so the
    // tool selection stays editable and isn't fixed to Core prematurely.
    startNewConversation()
  }

  const handleSelectConversation = (id: string) => {
    if (id !== currentConversationId) {
      loadMessages(id)
    }
  }

  const [selectedModel, setSelectedModel] = useState<string | null>(null)

  const handleSendMessage = async (messageText: string) => {
    await sendMessage(messageText, currentConversationId, selectedModel)
  }

  const handleRetryMessage = (messageId: string) => {
    retryMessage(messageId, selectedModel)
  }

  const handleUpdateConversation = async (conversationId: string, title: string) => {
    await updateConversationTitle(conversationId, title)
  }

  // Documentation handler
  const handleViewDocumentation = () => {
    window.open('https://docs.ecosemantic.com', '_blank', 'noopener,noreferrer')
  }

  // Call pack checkout mutation
  const callPackCheckoutMutation = useCallPackCheckout()

  // Call pack purchase handler
  const handlePurchaseCallPack = (
    paddlePriceId: string,
    packSize: number,
    productId: number,
    quantity?: number,
  ) => {
    if (!paddlePriceId) {
      toast.error('Invalid call pack - missing Paddle price ID')
      return
    }
    if (!productId) {
      toast.error('Invalid call pack - missing product ID')
      return
    }
    setPurchasingPackId(productId)
    callPackCheckoutMutation.mutate({ paddlePriceId, packSize, productId, quantity })
  }

  // Transform backend data to Dashboard format
  const userTier = userTierData
    ? {
        id: userTierData.id,
        name: userTierData.tier_name,
        displayName: formatTierName(userTierData.tier_name),
        monthlyLimit: usageData?.monthly_limit || 0,
        currentUsage: usageData?.current_month_usage || 0,
        databases: userTierData.databases || [],
        toolCount: (userTierData.tools || []).length,
      }
    : null

  // Show loading state
  if (
    tierLoading ||
    usageLoading ||
    activityLoading ||
    pricingLoading ||
    tokensLoading ||
    oauthTokensLoading ||
    tiersWithToolsLoading
  ) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Loading Dashboard...</div>
          <div className="text-sm text-muted-foreground">Fetching your account data</div>
        </div>
      </div>
    )
  }

  // Show error state - redirect to auth if 401
  if (tierError || usageError || activityError || pricingError || tokensError || !userTier) {
    // Check if any error is a 401 (unauthorized)
    const is401Error = [tierError, usageError, activityError, pricingError, tokensError].some(
      (error) =>
        error?.message?.includes('401') || error?.message?.toLowerCase().includes('unauthorized'),
    )

    if (is401Error) {
      // Redirect immediately to auth page
      navigate('/auth', { replace: true })
      return null
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Unable to Load Dashboard</CardTitle>
            <CardDescription>
              {tierError?.message ||
                usageError?.message ||
                activityError?.message ||
                pricingError?.message ||
                tokensError?.message ||
                'Failed to fetch user data'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // STEP 5: Real stats from usage data
  const stats = {
    currentUsage: usageData?.current_month_usage || 0,
    callPackBalance: usageData?.call_pack_balance || 0,
    totalAvailable: usageData?.total_available_calls || 0,
  }

  // Call pack / credit pricing from backend
  const callPacks = callPackPricingData ? callPackPricingData.map(mapCallPack) : []

  // Shared account status content (used in both desktop and mobile plan view)
  const renderPlanManagement = () => (
    <>
      {/* Active Promotion Notice */}
      {dashboardBalances?.active_tier_promotion && (
        <Alert variant="success">
          <Gift className="h-4 w-4" />
          <AlertTitle>Active Promotion</AlertTitle>
          <AlertDescription className="block">
            Bonus credits active.
            {dashboardBalances.active_tier_promotion.days_remaining > 0 && (
              <>
                {' '}
                Expires in{' '}
                <strong>{dashboardBalances.active_tier_promotion.days_remaining} days</strong>.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </>
  )

  // Shared usage/billing content
  const renderBillingContent = () => (
    <>
      {/* Buy Credits */}
      {callPacks.length > 0 &&
        (() => {
          const pack = callPacks[0]
          const totalCredits = creditQuantity * pack.size
          const totalPrice = creditQuantity * pack.price
          return (
            <Card className="overflow-hidden">
              <div className="grid sm:grid-cols-[1fr,auto] divide-y sm:divide-y-0 sm:divide-x divide-border">
                {/* Left: selector */}
                <div className="p-6 space-y-5">
                  <div>
                    <h3 className="font-semibold text-base">Add Credits</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      ${pack.price} per {pack.size} credits · valid 12 months
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Most users start with 200–500 credits. You can always buy more later.
                    </p>
                  </div>

                  {/* Quick select pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {[1, 2, 5, 10, 20, 50].map((q) => (
                      <button
                        key={q}
                        onClick={() => setCreditQuantity(q)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          creditQuantity === q
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                      >
                        {(q * pack.size).toLocaleString()}
                      </button>
                    ))}
                  </div>

                  {/* Stepper */}
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setCreditQuantity((q) => Math.max(1, q - 1))}
                      disabled={creditQuantity <= 1}
                    >
                      <span className="text-sm">−</span>
                    </Button>
                    <Input
                      type="number"
                      min={1}
                      max={50}
                      value={creditQuantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value)
                        if (!isNaN(v) && v >= 1 && v <= 50) setCreditQuantity(v)
                      }}
                      className="w-14 text-center text-sm font-semibold h-8"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setCreditQuantity((q) => Math.min(50, q + 1))}
                      disabled={creditQuantity >= 50}
                    >
                      <span className="text-sm">+</span>
                    </Button>
                    <span className="text-xs text-muted-foreground">units</span>
                  </div>
                </div>

                {/* Right: summary + buy */}
                <div className="p-6 flex flex-col items-center justify-center text-center space-y-4 sm:min-w-[200px] bg-muted/20">
                  <div className="space-y-1">
                    <div className="text-3xl font-bold tracking-tight">
                      {totalCredits.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground">credits</div>
                  </div>
                  <div className="text-xl font-semibold text-primary">${totalPrice.toFixed(2)}</div>
                  <Button
                    className="w-full"
                    onClick={() =>
                      handlePurchaseCallPack(
                        pack.priceId,
                        pack.size * creditQuantity,
                        pack.id,
                        creditQuantity,
                      )
                    }
                    disabled={callPackCheckoutMutation.isPending || !pack.priceId}
                  >
                    {callPackCheckoutMutation.isPending && purchasingPackId === pack.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Purchase'
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          )
        })()}

      {/* Credit Cost Guide */}
      <div className="rounded-xl border border-border/50 bg-card/50 p-5">
        <h4 className="text-sm font-semibold text-muted-foreground mb-4">How credits are used</h4>
        <div className="space-y-3">
          {/* MCP Tool calls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
                <Zap className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <span className="text-sm">Tool call</span>
                <p className="text-xs text-muted-foreground">
                  Any MCP tool (search, calculate, model…)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="h-2 rounded-full bg-primary/80" style={{ width: '12px' }} />
              <span className="text-xs font-semibold tabular-nums whitespace-nowrap">1 credit</span>
            </div>
          </div>
          {/* Chat model tiers — grouped by cost */}
          {chatModels &&
            chatModels.length > 0 &&
            (() => {
              const maxCost = Math.max(...chatModels.map((m: ChatModelTier) => m.credit_cost))
              const grouped = chatModels.reduce(
                (acc: Record<number, string[]>, m: ChatModelTier) => {
                  if (!acc[m.credit_cost]) acc[m.credit_cost] = []
                  acc[m.credit_cost].push(m.display_name)
                  return acc
                },
                {} as Record<number, string[]>,
              )
              const tierLabels: Record<number, string> = { 5: 'Fast', 10: 'Mid', 20: 'Premium' }
              return Object.entries(grouped)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([cost, names]) => {
                  const c = Number(cost)
                  const barWidth = Math.max(12, (c / maxCost) * 48)
                  const label = tierLabels[c] || `${c}-credit`
                  return (
                    <div key={cost} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm">{label} models</span>
                          <p className="text-xs text-muted-foreground truncate">
                            e.g. {(names as string[]).slice(0, 2).join(', ')}
                            {(names as string[]).length > 2 ? '…' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div
                          className="h-2 rounded-full bg-muted-foreground/30"
                          style={{ width: `${barWidth}px` }}
                        />
                        <span className="text-xs font-semibold tabular-nums whitespace-nowrap">
                          {c} credit{c > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )
                })
            })()}
        </div>
      </div>

      {/* Promo Code */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            Redeem Promo Code
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase())
                setPromoValidation(null)
              }}
              className="flex-1 uppercase"
              onKeyDown={(e) => e.key === 'Enter' && handleValidatePromo()}
            />
            <Button
              variant="outline"
              onClick={handleValidatePromo}
              disabled={!promoCode.trim() || isValidatingPromo}
            >
              {isValidatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validate'}
            </Button>
            <Button
              onClick={handleRedeemPromo}
              disabled={!promoValidation?.isValid || isRedeemingPromo}
            >
              {isRedeemingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Redeem'}
            </Button>
          </div>
          {promoValidation && (
            <p
              className={`text-sm mt-2 ${promoValidation.isValid ? 'text-success' : 'text-destructive'}`}
            >
              {promoValidation.message}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Purchase History */}
      {userTierData?.paddle_customer_id && (
        <Card>
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <h4 className="font-medium">Purchase History</h4>
              <p className="text-sm text-muted-foreground">View past transactions on Paddle</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManagePaddle}
              disabled={portalSessionMutation.isPending}
            >
              {portalSessionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                'Open Paddle Portal'
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </>
  )

  // Shared profile content
  const renderProfileContent = () => (
    <Card>
      <CardHeader>
        <CardTitle>Account Settings</CardTitle>
        <CardDescription>Manage your account preferences and security settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h4 className="font-medium">Profile Information</h4>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Full Name</label>
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    placeholder="Enter your name"
                    className="flex-1"
                    disabled={isSavingName}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName()
                      } else if (e.key === 'Escape') {
                        handleCancelEditName()
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveName}
                    disabled={isSavingName || !editedName.trim()}
                  >
                    {isSavingName ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelEditName}
                    disabled={isSavingName}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        onClick={handleStartEditName}
                        className="flex items-center justify-between group border border-transparent hover:border-muted rounded-md px-3 py-2 transition-colors cursor-pointer"
                      >
                        <p className="text-sm text-muted-foreground">
                          {userTierData?.name || 'Not set'}
                        </p>
                        <Pencil className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity text-muted-foreground" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Click to edit your name</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Email</label>
              <div className="flex items-center space-x-2">
                <p className="text-sm text-muted-foreground">
                  {userTierData?.email || 'user@example.com'}
                </p>
                {userTierData?.email_verified && (
                  <Badge variant="secondary" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Access</label>
              <p className="text-sm text-muted-foreground">Full access — all tools and databases</p>
            </div>
          </div>
        </div>

        <div className="border-t pt-6">
          <h4 className="font-medium mb-4">API Tokens</h4>
          <div className="space-y-3">
            {apiTokensData && apiTokensData.length > 0 ? (
              apiTokensData.map((token: TokenView) => (
                <div
                  key={token.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <h5 className="font-medium text-sm">{token.name || 'API Token'}</h5>
                    <p className="text-xs text-muted-foreground font-mono">
                      {token.token ? `${token.token.slice(0, 12)}••••••••` : '••••••••'}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Badge variant={token.is_active ? 'default' : 'secondary'}>
                      {token.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {token.token && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyToken(token.token)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setTokenToDelete(token.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-muted-foreground border rounded-lg">
                <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No API tokens yet</p>
              </div>
            )}
            <Button size="sm" onClick={() => setShowCreateTokenModal(true)}>
              <Key className="w-4 h-4 mr-2" />
              Generate New Token
            </Button>
          </div>
        </div>

        {/* OAuth Tokens Section (Read-only) */}
        {oauthTokensData && oauthTokensData.length > 0 && (
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium">OAuth Tokens</h4>
              <Badge variant="secondary" className="text-xs">
                Auto-managed
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              These tokens are automatically created when you connect MCP clients via OAuth. They
              cannot be manually deleted.
            </p>
            <div className="space-y-3">
              {oauthTokensData.map((token: TokenView) => (
                <div
                  key={token.id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/30"
                >
                  <div className="flex-1">
                    <h5 className="font-medium text-sm">{token.name}</h5>
                    <p className="text-xs text-muted-foreground font-mono">
                      {token.token ? `${token.token.slice(0, 12)}••••••••` : '••••••••'}
                    </p>
                    {token.last_used_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last used: {new Date(token.last_used_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Badge variant={token.is_active ? 'default' : 'secondary'}>
                      {token.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {token.token && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyToken(token.token)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copy token</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Preferences Section */}
        <div className="border-t pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5" />
            <h4 className="font-medium">Email Preferences</h4>
          </div>
          {emailPrefsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">Product updates</label>
                  <p className="text-xs text-muted-foreground">
                    New features, improvements, and platform news
                  </p>
                </div>
                <Switch
                  checked={emailPrefsData?.product_updates ?? true}
                  onCheckedChange={(v) => handleEmailPrefToggle('product_updates', v)}
                  disabled={updateEmailPrefsMutation.isPending}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">Feedback requests</label>
                  <p className="text-xs text-muted-foreground">
                    Surveys and opportunities to help improve EcoSemantic
                  </p>
                </div>
                <Switch
                  checked={emailPrefsData?.feedback_requests ?? true}
                  onCheckedChange={(v) => handleEmailPrefToggle('feedback_requests', v)}
                  disabled={updateEmailPrefsMutation.isPending}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">Promotions & offers</label>
                  <p className="text-xs text-muted-foreground">
                    Special offers, discounts, and promotional codes
                  </p>
                </div>
                <Switch
                  checked={emailPrefsData?.promotions ?? true}
                  onCheckedChange={(v) => handleEmailPrefToggle('promotions', v)}
                  disabled={updateEmailPrefsMutation.isPending}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">Newsletter</label>
                  <p className="text-xs text-muted-foreground">
                    Monthly digest with LCA insights and sustainability news
                  </p>
                </div>
                <Switch
                  checked={emailPrefsData?.newsletter ?? true}
                  onCheckedChange={(v) => handleEmailPrefToggle('newsletter', v)}
                  disabled={updateEmailPrefsMutation.isPending}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Essential account notifications (security, billing) are always sent.
              </p>
            </div>
          )}
        </div>

        {/* Data Export Section (GDPR Compliance) */}
        <div className="border-t pt-6">
          <h4 className="font-medium mb-4">Data & Privacy</h4>
          <div className="border rounded-lg p-4 space-y-3">
            <div>
              <h5 className="font-medium text-sm">Export Your Data</h5>
              <p className="text-sm text-muted-foreground mt-1">
                Download a complete copy of your personal data in JSON format. This includes your
                profile, conversations, usage statistics, and account information.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={handleExportData}>
              <Download className="w-4 h-4 mr-2" />
              Download My Data
            </Button>
          </div>
        </div>

        <div className="border-t pt-6">
          <h4 className="font-medium mb-4 text-destructive">Danger Zone</h4>
          <div className="border border-destructive/50 rounded-lg p-4 space-y-3">
            <div>
              <h5 className="font-medium text-sm">Delete Account</h5>
              <p className="text-sm text-muted-foreground mt-1">
                Permanently delete your account and all associated data. This action cannot be
                undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteAccountDialog(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="h-screen bg-background overflow-hidden">
      {/* Header - Sticky on mobile portrait, static on landscape and desktop */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky sm:static top-0 z-50 portrait:sticky landscape:static sm:landscape:static">
        <div className="flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Mobile Menu Button - In header */}
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button size="icon" variant="ghost" className="h-8 w-8">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SidebarContent
                  activeView={activeView}
                  setActiveView={(view) => {
                    setActiveView(view)
                    setSidebarOpen(false)
                  }}
                  stats={stats}
                  isCollapsed={false}
                  conversations={conversations}
                  currentConversationId={currentConversationId}
                  onSelectConversation={handleSelectConversation}
                  onNewConversation={handleNewConversation}
                  onDeleteConversation={deleteConversation}
                  onUpdateConversation={handleUpdateConversation}
                  isLoadingConversations={isLoadingConversations}
                  onNavigateToWorkspace={handleNavigateToWorkspace}
                />
              </SheetContent>
            </Sheet>

            {/* Desktop Logo */}
            <div className="hidden sm:block">
              <Logo size={32} showText={true} />
            </div>

            {/* Mobile Logo - Smaller with text */}
            <div className="sm:hidden">
              <Logo size={24} showText={true} />
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <Tooltip>
              <TooltipTrigger asChild className="hidden sm:inline-flex">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleViewDocumentation}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <BookOpen className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View documentation and guides</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Refresh usage data and statistics</p>
              </TooltipContent>
            </Tooltip>
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setActiveView('profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveView('usage')}>
                  <Coins className="mr-2 h-4 w-4" />
                  <span>Buy Credits</span>
                </DropdownMenuItem>
                {userTierData?.is_superuser && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Admin Panel</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-row h-[calc(100vh-64px)]">
        {/* Collapsed sidebar strip - shown when sidebar is collapsed (desktop only) */}
        {sidebarCollapsed && (
          <div className="hidden lg:block h-full">
            <CollapsedSidebarStrip
              onToggle={toggleSidebar}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          </div>
        )}

        {/* Resizable Panel Layout - Desktop only */}
        <div className="hidden lg:flex lg:flex-1 relative">
          <Allotment
            ref={allotmentRef}
            className="h-full"
            onChange={(sizes) => {
              if (sizes[0] && !sidebarCollapsed) {
                setSidebarWidth(sizes[0])
              }
            }}
          >
            {/* Desktop Sidebar Panel - Resizable */}
            {!sidebarCollapsed && (
              <Allotment.Pane
                preferredSize={sidebarWidth}
                minSize={SIDEBAR_CONSTRAINTS.minWidth}
                maxSize={SIDEBAR_CONSTRAINTS.maxWidth}
              >
                <aside className="h-full overflow-hidden border-r shadow-sm bg-background relative">
                  <div className="w-full h-full overflow-y-auto">
                    <SidebarContent
                      activeView={activeView}
                      setActiveView={setActiveView}
                      stats={stats}
                      isCollapsed={false}
                      conversations={conversations}
                      currentConversationId={currentConversationId}
                      onSelectConversation={handleSelectConversation}
                      onNewConversation={handleNewConversation}
                      onDeleteConversation={deleteConversation}
                      onUpdateConversation={handleUpdateConversation}
                      isLoadingConversations={isLoadingConversations}
                      onNavigateToWorkspace={handleNavigateToWorkspace}
                    />
                  </div>
                  {/* Notch toggle button - positioned at the right edge */}
                  <button
                    onClick={toggleSidebar}
                    className="absolute z-30 flex items-center justify-center w-6 h-12 right-0 top-1/2 -translate-y-1/2 bg-background border-2 border-border/60 rounded-l-md hover:bg-accent hover:border-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
                    title="Collapse sidebar"
                  >
                    <ChevronLeft className="h-5 w-5 text-muted-foreground" />
                  </button>
                </aside>
              </Allotment.Pane>
            )}

            {/* Main Content Panel */}
            <Allotment.Pane>
              <main className="h-full overflow-hidden bg-background">
                {activeView === 'chat' && (
                  <div className="h-full">
                    <ChatInterface
                      conversations={conversations}
                      currentConversationId={currentConversationId}
                      messages={messages}
                      streamingContent={streamingContent}
                      isStreaming={isStreaming}
                      isLoadingMessages={isLoadingMessages}
                      error={chatError}
                      failedMessageId={failedMessageId}
                      onSendMessage={handleSendMessage}
                      onRetryMessage={handleRetryMessage}
                      selectedModel={selectedModel}
                      onModelChange={setSelectedModel}
                    />
                  </div>
                )}

                {activeView === 'tools' && (
                  <div className="h-full overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
                    <div className="max-w-7xl mx-auto">
                      {tiersWithToolsData && (
                        <AvailableToolsTab tiersData={tiersWithToolsData.tiers} />
                      )}
                    </div>
                  </div>
                )}

                {activeView === 'usage' && (
                  <div className="h-full overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
                    <div className="max-w-7xl mx-auto space-y-6">{renderBillingContent()}</div>
                  </div>
                )}

                {activeView === 'plan' && (
                  <div className="h-full overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
                    <div className="max-w-7xl mx-auto space-y-6">
                      <PlanOverviewContent
                        stats={stats}
                        userTier={userTier}
                        creditUsageBreakdown={creditUsageBreakdown}
                      />
                      {renderPlanManagement()}
                      {creditUsageHistoryData && (
                        <CreditUsageHistory data={creditUsageHistoryData} />
                      )}
                      {creditLedgerData && <CreditLedger data={creditLedgerData} />}
                    </div>
                  </div>
                )}

                {activeView === 'profile' && (
                  <div className="h-full overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
                    <div className="max-w-7xl mx-auto">{renderProfileContent()}</div>
                  </div>
                )}

              </main>
            </Allotment.Pane>
          </Allotment>
        </div>

        {/* Mobile Main Content - shown on smaller screens */}
        <main className="flex-1 overflow-hidden h-full bg-background lg:hidden">
          {activeView === 'chat' && (
            <div className="h-full">
              <ChatInterface
                conversations={conversations}
                currentConversationId={currentConversationId}
                messages={messages}
                streamingContent={streamingContent}
                isStreaming={isStreaming}
                isLoadingMessages={isLoadingMessages}
                error={chatError}
                failedMessageId={failedMessageId}
                onSendMessage={handleSendMessage}
                onRetryMessage={handleRetryMessage}
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
              />
            </div>
          )}

          {activeView === 'tools' && (
            <div className="h-full overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
              <div className="max-w-7xl mx-auto">
                {tiersWithToolsData && (
                  <AvailableToolsTab tiersData={tiersWithToolsData.tiers} />
                )}
              </div>
            </div>
          )}

          {activeView === 'plan' && (
            <div className="h-full overflow-y-auto px-4 sm:px-6 py-6 sm:py-8">
              <div className="max-w-7xl mx-auto space-y-6">
                <PlanOverviewContent
                  stats={stats}
                  userTier={userTier}
                  creditUsageBreakdown={creditUsageBreakdown}
                />
                {renderPlanManagement()}
                {creditUsageHistoryData && <CreditUsageHistory data={creditUsageHistoryData} />}
                {creditLedgerData && <CreditLedger data={creditLedgerData} />}
              </div>
            </div>
          )}

          {activeView === 'usage' && (
            <div className="h-full overflow-y-auto px-4 py-6">
              <div className="max-w-7xl mx-auto space-y-6">{renderBillingContent()}</div>
            </div>
          )}

          {activeView === 'profile' && (
            <div className="h-full overflow-y-auto px-4 py-6">
              <div className="max-w-7xl mx-auto">{renderProfileContent()}</div>
            </div>
          )}

        </main>
      </div>

      {/* Create Token Modal */}
      <Dialog open={showCreateTokenModal} onOpenChange={setShowCreateTokenModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New API Token</DialogTitle>
            <DialogDescription>
              Choose a descriptive name to help you identify this token later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="token-name">Token Name</Label>
              <Input
                id="token-name"
                placeholder="e.g., Production Server, Development"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateToken()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTokenModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateToken}
              disabled={createTokenMutation.isPending || !tokenName.trim()}
            >
              {createTokenMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Token Display Modal (Only shown once!) */}
      <Dialog open={showTokenDisplayModal} onOpenChange={setShowTokenDisplayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-success mr-2" />
              Token Created Successfully
            </DialogTitle>
            <DialogDescription>
              Copy this token now. For security, it won't be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Token Name</Label>
              <div className="font-mono text-sm">{newTokenData?.name}</div>
            </div>
            <div className="space-y-2">
              <Label>API Token</Label>
              <div className="flex items-center space-x-2">
                <Input value={newTokenData?.token || ''} readOnly className="font-mono text-xs" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => newTokenData?.token && handleCopyToken(newTokenData.token)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                Store this token securely. You won't be able to see it again.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTokenDisplayModal(false)}>I've Saved My Token</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Token Confirmation */}
      <AlertDialog open={!!tokenToDelete} onOpenChange={() => setTokenToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Token?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Any applications using this token will stop working
              immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => tokenToDelete && deleteTokenMutation.mutate(tokenToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTokenMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Token'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activity Details Dialog */}
      <Dialog open={!!selectedActivity} onOpenChange={() => setSelectedActivity(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tool Usage Details</DialogTitle>
            <DialogDescription>Detailed information about this MCP tool call</DialogDescription>
          </DialogHeader>
          {selectedActivity &&
            (() => {
              // Parse request parameters from the stored JSON
              let requestParams: Record<string, unknown> = {}
              try {
                if (selectedActivity.request_params) {
                  requestParams =
                    typeof selectedActivity.request_params === 'string'
                      ? JSON.parse(selectedActivity.request_params)
                      : selectedActivity.request_params
                }
              } catch (e) {
                console.error('Failed to parse request params:', e)
              }

              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Tool</label>
                      <p className="text-sm text-muted-foreground">
                        {selectedActivity.tool_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Database</label>
                      <p className="text-sm text-muted-foreground">
                        {selectedActivity.database_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Status</label>
                      <Badge variant={selectedActivity.success ? 'success' : 'destructive'}>
                        {selectedActivity.success ? 'Success' : 'Failed'}
                      </Badge>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Timestamp</label>
                      <p className="text-sm text-muted-foreground">
                        {new Date(selectedActivity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {Object.keys(requestParams).length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Input Parameters</label>
                      <div className="bg-muted p-3 rounded space-y-2 max-h-64 overflow-y-auto">
                        {Object.entries(requestParams).map(([key, value]) => (
                          <div key={key} className="grid grid-cols-3 gap-2 text-xs">
                            <span className="font-medium text-muted-foreground">{key}:</span>
                            <span className="col-span-2 text-foreground font-mono break-all">
                              {value === null
                                ? 'null'
                                : typeof value === 'object'
                                  ? JSON.stringify(value)
                                  : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedActivity.error_message && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-destructive">Error Message</label>
                      <p className="text-sm text-destructive bg-destructive/10 p-3 rounded">
                        {selectedActivity.error_message}
                      </p>
                    </div>
                  )}

                  {selectedActivity.response_time_ms && (
                    <div>
                      <label className="text-sm font-medium">Response Time</label>
                      <p className="text-sm text-muted-foreground">
                        {selectedActivity.response_time_ms}ms
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium">Status Code</label>
                    <p className="text-sm text-muted-foreground">{selectedActivity.status_code}</p>
                  </div>
                </div>
              )
            })()}
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center text-destructive">
              <AlertCircle className="w-5 h-5 mr-2" />
              Delete Account Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-3 mb-3">
                <p className="font-bold text-yellow-800 dark:text-yellow-200 text-sm mb-1">
                  ⚠️ IMPORTANT: Export your data first!
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 text-xs">
                  After deletion, you cannot access your chat history. Use "Download My Data" in
                  Account Settings to save your conversations before proceeding.
                </p>
              </div>
              <p>
                This action <strong>cannot be undone</strong>. This will permanently delete your
                account and remove all your data from our servers, including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Your profile and account information</li>
                <li>Access to all conversations and chat history (data anonymized and retained)</li>
                <li>API tokens and access credentials</li>
                <li>Usage statistics and activity logs</li>
                <li>Billing data (retained for tax compliance)</li>
              </ul>
              <p className="font-medium">Your account will be permanently deleted.</p>
              <div className="pt-4">
                <Label htmlFor="delete-confirm">
                  Type <strong>DELETE</strong> to confirm:
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || deleteAccountMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteAccountMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Account
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Goodbye Dialog */}
      <Dialog open={showGoodbyeDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">We're Sad to See You Go</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center">
              <CheckCircle className="w-16 h-16 text-success" />
            </div>
            <div className="text-center space-y-3">
              <p className="font-medium text-lg">Your account has been successfully deleted</p>
              <p className="text-muted-foreground">
                We're continuously innovating at the intersection of Life Cycle Assessment and AI.
                Our mission is to make sustainability insights more accessible and actionable for
                everyone.
              </p>
              <p className="text-muted-foreground">
                We're working hard to improve our tools and expand our capabilities. We hope to
                welcome you back in the future when we can offer you an even better set of tools for
                your sustainability journey.
              </p>
              <p className="font-medium">
                Thank you for being part of the EcoSemantic community! 🌱
              </p>
            </div>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button onClick={handleGoodbyeComplete} className="w-full sm:w-auto">
              Continue to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Getting Started Panel — disabled, pending redesign */}
      {showGettingStartedPanel && (
        <GettingStartedPanel
          userTierData={userTierData}
          usageData={usageData}
          creditUsageBreakdown={creditUsageBreakdown}
          activeView={activeView}
          onNavigateToView={setActiveView}
          onNavigateToWorkspace={handleNavigateToWorkspace}
        />
      )}
    </div>
  )
}

export default Dashboard
